var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useTheme } from "@mui/material";
import { LogLevel } from "../../../Shared/hooks/useLogging/Logging-types";
import { CardFormText, PaymentProcessor, StatusType, } from "../Shared/types/PaymentProcessorTypes";
export const GetStripeAppearance = () => {
    const theme = useTheme();
    return {
        labels: "floating",
        variables: {
            colorPrimary: theme.palette.primary.main,
        },
        rules: {
            ".Input:hover, .Input:focus": {
                border: `1px solid ${theme.palette.primary.main}`,
            },
        },
    };
};
/**
 * Closes modal and either resets the form fields or re-fetches billing information
 */
export const handleClose = (resetFields, clearErrors, cardFormState, setCardFormState) => (formLoading, setAddedNewCard, onClose) => () => {
    if (!formLoading && !cardFormState.submitted) {
        onClose();
        resetFields();
        clearErrors();
    }
    if (formLoading && cardFormState.submitted) {
        setAddedNewCard(true);
        resetFields();
        clearErrors();
        onClose();
        setCardFormState({
            status: Object.assign(Object.assign({}, cardFormState.status), { type: StatusType.IDLE }),
            submitted: false,
            loading: false,
        });
    }
};
/**
 * Resets all form fields
 */
export const resetFields = (resetField) => () => {
    resetField("cardHolderName");
    resetField("isPrimaryChecked");
};
/**
 * Update an existing payment method
 * @param updatePaymentMethod
 * @param setCardFormState
 * @param body
 * @param paymentMethodId
 */
export const handleUpdate = (updatePaymentMethod, setCardFormState, body, paymentMethodId) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        yield updatePaymentMethod(body);
        setCardFormState({
            loading: true,
            submitted: true,
            status: {
                type: StatusType.SUCCEEDED,
                message: CardFormText.YOUR_CARD_WAS_UPDATED,
            },
        });
    }
    catch (error) {
        setCardFormState({
            loading: true,
            submitted: true,
            status: {
                type: StatusType.ERROR,
                message: `${(error === null || error === void 0 ? void 0 : error.message) || "Failed to update"} ${CardFormText.PLEASE_TRY_AGAIN}`,
            },
        });
    }
});
/**
 * Add a new payment method
 * @param createPaymentMethod
 * @param setCardFormState
 * @param body
 */
export const handleAdd = (createPaymentMethod, setCardFormState, body) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        yield createPaymentMethod(body);
        setCardFormState({
            loading: true,
            submitted: true,
            status: {
                type: StatusType.SUCCEEDED,
                message: CardFormText.YOUR_CARD_WAS_ADDED,
            },
        });
    }
    catch (error) {
        setCardFormState({
            loading: true,
            submitted: true,
            status: {
                type: StatusType.ERROR,
                message: `${(error === null || error === void 0 ? void 0 : error.message) || "Failed to add"} ${CardFormText.PLEASE_TRY_AGAIN}`,
            },
        });
    }
});
/**
 * Submit stripe form to either add or update a payment method
 * @param stripe
 * @param elements
 * @param setSubmitted
 * @param setLoading
 * @param setStatus
 * @param createPaymentMethod
 * @param handleUpdatePayment
 * @param selectedCard
 * @returns
 */
export const onSubmit = (stripe, elements, setCardFormState, createPaymentMethod, updatePaymentMethod, datadogLog, selectedCard) => (data) => __awaiter(void 0, void 0, void 0, function* () {
    if (!stripe || !elements) {
        // Stripe.js has not yet loaded.
        // Make sure to disable form submission until Stripe.js has loaded.
        return;
    }
    stripe
        .confirmSetup({
        elements,
        confirmParams: {
            return_url: `${window.location.origin}/Account/StripePaymentStatus`,
            payment_method_data: {
                billing_details: {
                    name: data.cardHolderName,
                },
            },
        },
        redirect: "if_required",
    })
        .then((res) => {
        var _a, _b, _c, _d, _e, _f;
        if (((_a = res.setupIntent) === null || _a === void 0 ? void 0 : _a.status) === StatusType.SUCCEEDED) {
            const paymentMethodRequestBody = {
                paymentMethodId: selectedCard === null || selectedCard === void 0 ? void 0 : selectedCard.id,
                isPrimary: data.isPrimaryChecked,
                paymentProcessor: PaymentProcessor.STRIPE,
                stripePaymentMethodId: typeof ((_b = res === null || res === void 0 ? void 0 : res.setupIntent) === null || _b === void 0 ? void 0 : _b.payment_method) === "string"
                    ? res.setupIntent.payment_method
                    : ((_d = (_c = res.setupIntent.payment_method) === null || _c === void 0 ? void 0 : _c.id) !== null && _d !== void 0 ? _d : null),
            };
            setCardFormState({
                loading: true,
                submitted: true,
                status: {
                    message: (selectedCard === null || selectedCard === void 0 ? void 0 : selectedCard.id) ? CardFormText.UPDATE_LOADING_TEXT : CardFormText.ADD_LOADING_TEXT,
                    type: StatusType.PROCESSING,
                },
            });
            if (selectedCard === null || selectedCard === void 0 ? void 0 : selectedCard.id) {
                handleUpdate(updatePaymentMethod, setCardFormState, paymentMethodRequestBody, selectedCard.id);
            }
            else {
                handleAdd(createPaymentMethod, setCardFormState, paymentMethodRequestBody);
            }
        }
        if (res.error) {
            const message = (_f = (_e = res.error) === null || _e === void 0 ? void 0 : _e.message) !== null && _f !== void 0 ? _f : CardFormText.UNEXPECTED_ERROR;
            datadogLog(message, LogLevel.ERROR);
            setCardFormState({
                loading: true,
                submitted: true,
                status: {
                    type: StatusType.ERROR,
                    message: `${message} ${CardFormText.PLEASE_TRY_AGAIN}`,
                },
            });
        }
    })
        .catch((err) => {
        datadogLog(err.message, LogLevel.ERROR, undefined, err);
        setCardFormState({
            loading: true,
            submitted: true,
            status: {
                type: StatusType.ERROR,
                message: `${CardFormText.UNEXPECTED_ERROR} ${CardFormText.PLEASE_TRY_AGAIN}`,
            },
        });
        console.error(err);
    });
});
